import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../api/api';

const ProjectContext = createContext(null);

export const useProject = () => {
    const ctx = useContext(ProjectContext);
    if (!ctx) throw new Error('useProject must be used within a ProjectProvider');
    return ctx;
};

export const ProjectProvider = ({ children }) => {
    const location = useLocation();
    const [projects, setProjects] = useState([]);
    const [globalTheme, setGlobalTheme] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeProject, setActiveProject] = useState(null);

    // Local dev is distinguished from live by hostname.
    const isLocal = useMemo(() => (
        window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ), []);

    // Lightweight public project list (id + name + slug + domain) used to map
    // local URL path segments (or the live hostname) to a project.
    useEffect(() => {
        let mounted = true;
        api.get('/api/web/projects')
            .then(res => { 
                if (mounted) {
                    setProjects(res.data?.data || []); 
                    setGlobalTheme(res.data?.globalTheme || null);
                }
            })
            .catch(() => {})
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, []);

    // Normalize a host/domain string for robust matching
    const normalizeDomain = (input = '') => {
        let host = String(input).trim().toLowerCase();
        host = host.replace(/^https?:\/\//i, '');
        host = host.replace(/:\d+$/, '');
        host = host.replace(/\/+$/, '');
        host = host.replace(/^www\./, '');
        return host;
    };

    // Resolve the active project from the URL (local) or hostname (live).
    useEffect(() => {
        if (loading) return;
        const segments = location.pathname.split('/').filter(Boolean);
        let match = null;
        if (isLocal && segments[0]) {
            let projectIdentifier = segments[0];
            if ((segments[0] === 'projects' || segments[0] === 'p') && segments.length > 1) {
                projectIdentifier = segments[1];
            }
            match = projects.find(p => p.slug === projectIdentifier)
                || projects.find(p => p.domain && normalizeDomain(p.domain) === normalizeDomain(projectIdentifier))
                || null;
        } else if (!isLocal) {
            const currentHost = normalizeDomain(window.location.hostname);
            match = projects.find(p => p.domain && normalizeDomain(p.domain) === currentHost) || null;
        }
        setActiveProject(match);

        // Apply theme variables for public site
        const applyThemeSettings = (settings) => {
            const variables = [
                'primary_lightest', 'primary_lighter', 'primary_light', 'primary', 'primary_dark', 'primary_darker', 'primary_darkest', 'primary_color', 'primary_bg', 'primary_bg_light', 'primary_rgb',
                'secondary_lightest', 'secondary_lighter', 'secondary_light', 'secondary', 'secondary_dark', 'secondary_darker', 'secondary_darkest', 'secondary_color', 'secondary_bg', 'secondary_bg_light', 'secondary_rgb',
                'page_background', 'content_background', 'popup_background',
                'header_text', 'header_hover', 'header_pressed', 'header_background', 'footer_text', 'footer_hover', 'footer_pressed', 'footer_background',
                'heading_text', 'heading_text_background', 'paragraph_text', 'paragraph_text_background',
                'link_color', 'hovered_link_color', 'pressed_link_color'
            ];

            let cssString = '';

            const generateRules = (selector, themeData) => {
                if (!themeData) return '';
                let rules = '';
                variables.forEach(v => {
                    if (themeData[v]) {
                        rules += `  --${v.replace(/_/g, '-')}: ${themeData[v]};\n`;
                    }
                });
                if (themeData.font_size) {
                    rules += `  --base-font-size: ${themeData.font_size};\n`;
                    rules += `  --font-size: ${themeData.font_size};\n`;
                }
                if (themeData.font_family) {
                    rules += `  --font-family: '${themeData.font_family}', sans-serif;\n`;
                }
                if (!rules) return '';
                return `${selector} {\n${rules}}\n`;
            };

            cssString += generateRules(':root', settings);
            
            if (settings.responsive && settings.responsive.tablet) {
                cssString += `@media (max-width: 1024px) {\n${generateRules(':root', settings.responsive.tablet)}}\n`;
            }
            if (settings.responsive && settings.responsive.mobile) {
                cssString += `@media (max-width: 768px) {\n${generateRules(':root', settings.responsive.mobile)}}\n`;
            }

            let styleEl = document.getElementById('live-dynamic-theme');
            if (!styleEl) {
                styleEl = document.createElement('style');
                styleEl.id = 'live-dynamic-theme';
                document.head.appendChild(styleEl);
            }
            styleEl.innerHTML = cssString;

            // Remove legacy inline styles if they exist
            const root = document.documentElement;
            variables.forEach(v => root.style.removeProperty(`--${v.replace(/_/g, '-')}`));
            root.style.removeProperty('--base-font-size');
            root.style.removeProperty('--font-size');
            root.style.removeProperty('--font-family');
        };

        if (match) {
            let settings = {};
            try {
                settings = typeof match.settings_live === 'string' 
                    ? JSON.parse(match.settings_live) 
                    : (match.settings_live || {});
            } catch (e) {}
            applyThemeSettings(settings);
        } else {
            let settings = {};
            if (globalTheme) {
                try {
                    settings = typeof globalTheme === 'string' 
                        ? JSON.parse(globalTheme) 
                        : (globalTheme || {});
                } catch (e) {}
            }
            applyThemeSettings(settings);
        }
    }, [loading, projects, location.pathname, isLocal, globalTheme]);

    const value = useMemo(() => ({
        projects,
        isLocal,
        activeProject,
        projectId: activeProject ? String(activeProject.id) : null,
        isProjectLoading: loading,
    }), [projects, isLocal, activeProject, loading]);

    return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
};

export default ProjectContext;